import { TPSScores, AlignmentDetail } from '../types/tps.types';
import { calculateConfidence } from './utils';
import { getWeight } from './weightLoader';

/**
 * Differential two-axis alignment model.
 * Each axis has positive-leaning and negative-leaning traits with weights.
 * Net score determines position instead of three-way bucket competition.
 */

export interface AlignmentTraitWeight {
  trait: string;
  weight: number;
}

export interface AlignmentAxisConfig {
  positiveName: string;   // e.g. 'Lawful' or 'Good'
  negativeName: string;   // e.g. 'Chaotic' or 'Evil'
  positiveTraits: AlignmentTraitWeight[];
  negativeTraits: AlignmentTraitWeight[];
  threshold: number;      // net score beyond which we assign non-neutral
}

/**
 * Net differential (in trait units on the 1-10 trait scale, i.e. deviation
 * from the 5.5 midpoint) beyond which an axis leaves the neutral band.
 *
 * Calibrated against the production trait distribution: net differentials run
 * to roughly ±2.5 with sd ≈ 1.1, so 1.0 (≈0.9 sd) keeps True Neutral for
 * genuinely balanced profiles while making all nine alignments reachable.
 * Previously the axis was normalised by trait *count* instead of weight sum,
 * which shrank every differential below the old 0.4 threshold and pushed 91%
 * of stored profiles to True Neutral.
 */
export const ALIGNMENT_THRESHOLD = 1.0;

/**
 * Response-style standardisation.
 *
 * A raw net differential is only comparable between two respondents who answer
 * with the same extremity. Simulated personas answer near the rails (mean
 * absolute deviation from the 5.5 midpoint ≈ 1.7–3.5); real users answer much
 * closer to the middle. Any raw cut point is therefore a *different* cut for
 * the two populations, which is why the hand-set 1.0 above both mislabelled
 * personas (41% exact) and pushed real profiles toward True Neutral.
 *
 * Dividing the net by the respondent's own spread removes the style term, so
 * the bands below transfer between populations.
 */
export const alignmentResponseSpread = (scores: TPSScores): number => {
  const values = Object.values(scores).filter((v): v is number => typeof v === 'number');
  if (!values.length) return 1;
  const spread = values.reduce((sum, v) => sum + Math.abs(v - 5.5), 0) / values.length;
  // Floor keeps a flat-lining respondent from producing an exploding ratio.
  return Math.max(spread, 0.6);
};

export interface AlignmentBand {
  /** At or below this standardised net, the negative pole is awarded. */
  low: number;
  /** At or above this standardised net, the positive pole is awarded. */
  high: number;
}

/**
 * Max-margin bands fitted on the 31 alignment personas of the 223-persona
 * sweep, using standardised nets. Each band separates all three positions of
 * its axis without error, and together they recover 31/31 nine-way labels —
 * against 41% exact for the previous symmetric ±1.0 on raw nets, whose best
 * achievable raw setting on the same corpus was only 48%.
 *
 * The bands are asymmetric because the axes are: a genuinely neutral respondent
 * sits about 0.2 standardised units below zero on both.
 */
export const ETHICAL_BAND: AlignmentBand = { low: -1.74, high: 1.36 };
export const MORAL_BAND: AlignmentBand = { low: -0.8, high: 0.4 };

/** Standardised distance inside which a pole call is a hairline, not a position. */
export const ALIGNMENT_HAIRLINE = 0.15;

export const ETHICAL_AXIS: AlignmentAxisConfig = {
  positiveName: 'Lawful',
  negativeName: 'Chaotic',
  positiveTraits: [
    { trait: 'Structured', weight: 2 },
    { trait: 'Lawful', weight: 2 },
    { trait: 'Self-Mastery', weight: 1.5 },
    { trait: 'Analytical', weight: 1 },
    { trait: 'Stoic', weight: 1 },
    { trait: 'Passive', weight: 0.5 },
  ],
  negativeTraits: [
    { trait: 'Self-Principled', weight: 2 },
    { trait: 'Independent', weight: 2 }, // TPS-EXEMPT: Independent=interpersonal.navigate.independent
    { trait: 'Dynamic', weight: 1.5 },
    { trait: 'Intuitive', weight: 1 },
    { trait: 'Varied', weight: 1 },
    { trait: 'Self-Indulgent', weight: 0.5 },
  ],
  threshold: ALIGNMENT_THRESHOLD,
};

export const MORAL_AXIS: AlignmentAxisConfig = {
  positiveName: 'Good',
  negativeName: 'Evil',
  // Re-keyed against the alignment personas. The previous key measured "not
  // warm" rather than Evil: `Independent Navigate` (weight 2) scored 10.0 for
  // Evil personas and 9.49 for Neutral ones, so every Lawful/Chaotic Neutral
  // persona landed deep in Evil territory. `Diplomatic` (1.5) was dead
  // (5.84 / 5.74 / 5.48 across Good / Neutral / Evil), `Responsive` (1.5) ran
  // backwards (Good 4.50 < Neutral 5.84), and `Extrinsic` / `Intrinsic` were on
  // the wrong sides outright (Extrinsic: Good 1.41, Neutral 2.22, Evil 5.96;
  // Intrinsic: Good 9.46, Neutral 7.78, Evil 5.05).
  //
  // Every trait kept below is monotone across Good → Neutral → Evil on that
  // corpus, which is what lets a single band separate all three positions.
  positiveTraits: [
    { trait: 'Communal Navigate', weight: 2 },
    { trait: 'Social', weight: 1.5 }, // TPS-EXEMPT: Social=processing.reality.social
    { trait: 'Intrinsic', weight: 1.5 },
    { trait: 'Universal', weight: 1 },
    { trait: 'Self-Principled', weight: 1 },
    { trait: 'Passive Communication', weight: 0.5 },
    { trait: 'Mixed Navigate', weight: 0.5 },
  ],
  negativeTraits: [
    { trait: 'Assertive', weight: 1.5 },
    { trait: 'Extrinsic', weight: 1.5 },
    { trait: 'Physical', weight: 1 },
    { trait: 'Self-Indulgent', weight: 1 },
    { trait: 'Independent Navigate', weight: 0.5 },
    { trait: 'Pessimistic', weight: 0.5 },
  ],
  threshold: ALIGNMENT_THRESHOLD,
};

export interface AlignmentTraitContribution {
  trait: string;
  side: 'positive' | 'negative';
  score: number;
  weight: number;
  contribution: number;
}

export interface AlignmentAxisAudit {
  position: string;
  score: number;
  netScore: number;
  threshold: number;
  reasoning: string;
  contributions: AlignmentTraitContribution[];
}

export interface AlignmentAxisAuditExtras {
  /** Net differential divided by the respondent's own response spread. */
  standardisedNet: number;
  /** The fitted band this axis was compared against. */
  band: AlignmentBand;
  /** Response spread (mean absolute deviation from 5.5) used to standardise. */
  spread: number;
  /** Standardised distance from the deciding band edge; negative inside the band. */
  bandDistance: number;
  /** True when the call sits within `ALIGNMENT_HAIRLINE` of an edge. */
  hairline: boolean;
}

export function calculateAlignmentAxisAudit(
  scores: TPSScores,
  axis: 'ethical' | 'moral',
): AlignmentAxisAudit & AlignmentAxisAuditExtras {
  const config = axis === 'ethical' ? ETHICAL_AXIS : MORAL_AXIS;
  const band = axis === 'ethical' ? ETHICAL_BAND : MORAL_BAND;
  let positivePull = 0;
  let negativePull = 0;
  let positiveWeight = 0;
  let negativeWeight = 0;

  // Positive traits push score upward (toward positiveName)
  const contributions: AlignmentTraitContribution[] = [];
  for (const { trait, weight: defaultWeight } of config.positiveTraits) {
    const value = scores[trait];
    if (value === undefined) continue;
    const weight = getWeight('Alignment', axis, trait, defaultWeight);
    positivePull += (value - 5.5) * weight;
    positiveWeight += weight;
    contributions.push({ trait, side: 'positive', score: value, weight, contribution: (value - 5.5) * weight });
  }

  // Negative traits push score downward (toward negativeName)
  for (const { trait, weight: defaultWeight } of config.negativeTraits) {
    const value = scores[trait];
    if (value === undefined) continue;
    const weight = getWeight('Alignment', axis, trait, defaultWeight);
    negativePull += (value - 5.5) * weight;
    negativeWeight += weight;
    contributions.push({ trait, side: 'negative', score: value, weight, contribution: -(value - 5.5) * weight });
  }

  // Each side is normalised by its OWN weight sum, so both pulls are expressed
  // in trait units (deviation from the 5.5 midpoint, range -4.5..+4.5).
  // The net differential is therefore also in trait units (-9..+9 worst case,
  // realistically ±3), which the threshold below is calibrated against.
  const positiveMean = positiveWeight > 0 ? positivePull / positiveWeight : 0;
  const negativeMean = negativeWeight > 0 ? negativePull / negativeWeight : 0;
  const netScore = positiveMean - negativeMean;
  const spread = alignmentResponseSpread(scores);
  const standardisedNet = netScore / spread;

  let position: string;
  let reasoning: string;

  if (standardisedNet >= band.high) {
    position = config.positiveName;
    reasoning = `Strong ${config.positiveName.toLowerCase()} tendencies from trait pattern (standardised differential: ${standardisedNet.toFixed(2)})`;
  } else if (standardisedNet <= band.low) {
    position = config.negativeName;
    reasoning = `Strong ${config.negativeName.toLowerCase()} tendencies from trait pattern (standardised differential: ${standardisedNet.toFixed(2)})`;
  } else {
    position = 'Neutral';
    reasoning = `Balanced between ${config.positiveName.toLowerCase()} and ${config.negativeName.toLowerCase()} tendencies (standardised differential: ${standardisedNet.toFixed(2)})`;
  }

  const bandDistance =
    position === config.positiveName
      ? standardisedNet - band.high
      : position === config.negativeName
        ? band.low - standardisedNet
        : -Math.min(band.high - standardisedNet, standardisedNet - band.low);

  // Map net score (trait units) to a 1-10 display scale: 0 → 5.5, ±4.5 → 1/10
  const displayScore = Math.max(1, Math.min(10, 5.5 + netScore));

  return {
    position,
    score: displayScore,
    netScore,
    // Reported in raw trait units so existing diagnostics that compare it with
    // `netScore` keep meaning what they say.
    threshold: (position === config.negativeName ? Math.abs(band.low) : band.high) * spread,
    reasoning,
    standardisedNet: +standardisedNet.toFixed(3),
    band,
    spread: +spread.toFixed(3),
    bandDistance: +bandDistance.toFixed(3),
    hairline: Math.abs(bandDistance) < ALIGNMENT_HAIRLINE,
    contributions: contributions.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution)),
  };
}

export function calculateAlignmentEnhanced(scores: TPSScores): AlignmentDetail {
  const ethicalResult = calculateAlignmentAxisAudit(scores, 'ethical');
  const moralResult = calculateAlignmentAxisAudit(scores, 'moral');

  // Determine final alignment string
  let alignment: string;
  if (ethicalResult.position === 'Neutral' && moralResult.position === 'Neutral') {
    alignment = 'True Neutral';
  } else if (ethicalResult.position === 'Neutral') {
    alignment = `Neutral ${moralResult.position}`;
  } else if (moralResult.position === 'Neutral') {
    alignment = `${ethicalResult.position} Neutral`;
  } else {
    alignment = `${ethicalResult.position} ${moralResult.position}`;
  }

  // Confidence rides on how far each axis sits from the edge that decided it,
  // in the same standardised units the position was decided on.
  const ethicalConfidence = calculateConfidence(Math.abs(ethicalResult.bandDistance), 0.5);
  const moralConfidence = calculateConfidence(Math.abs(moralResult.bandDistance), 0.5);
  const overallConfidence = (ethicalConfidence + moralConfidence) / 2;

  // A hairline on either axis caps confidence: the label could flip on one item.
  const isNearCenter = ethicalResult.hairline || moralResult.hairline;

  return {
    alignment,
    ethicalAxis: {
      position: ethicalResult.position as 'Lawful' | 'Neutral' | 'Chaotic',
      score: ethicalResult.score,
      reasoning: ethicalResult.reasoning,
    },
    moralAxis: {
      position: moralResult.position as 'Good' | 'Neutral' | 'Evil',
      score: moralResult.score,
      reasoning: moralResult.reasoning,
    },
    confidence: isNearCenter ? Math.min(overallConfidence, 0.5) : overallConfidence,
  };
}

// Keep export for backward compat (no longer used internally but may be referenced)
export const TPS_TO_ALIGNMENT_COMPREHENSIVE = {
  ethical_axis: ETHICAL_AXIS,
  moral_axis: MORAL_AXIS,
};
